import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { merge, Observable, Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, share, startWith, withLatestFrom } from 'rxjs/operators';
import { Favorite } from 'src/app/shared/models';
import { CSSInset } from 'src/app/shared/models/misc.model';
import { BasicService } from 'src/app/shared/services/basic.service';
import { StringSearchService } from 'src/app/shared/services/string-search.service';

import { CodemirrorContext } from '../codemirror-context';
import { FavoriteSuggestion } from './favorite-suggestion.class';

// gitlab #4407: Suggestion de réponses favorites
@Component({
  selector: 'app-favorite-suggestion',
  templateUrl: './favorite-suggestion.component.html',
  styleUrls: ['./favorite-suggestion.component.scss'],
})
export class FavoriteSuggestionComponent implements OnInit, OnDestroy {
  @Input() favorites: Favorite[];
  @Input() codemirrorContext: CodemirrorContext;
  @Output() favoriteSelected = new EventEmitter<Favorite>();

  suggestions$: Observable<FavoriteSuggestion[]>;

  isPopoverDisplayed$: Observable<boolean>;
  previewedInEditor$: Observable<FavoriteSuggestion | undefined>;
  insets$: Observable<{
    popover: CSSInset;
    preview: CSSInset;
  }>;

  private dismissed$ = new Subject<void>();
  private subscriptions = new Subscription();
  private validTermRe = /^[a-zA-Z0-9 ',.?!-]{3,20}$/;

  constructor(
    private elementRef: ElementRef,
    private basicService: BasicService,
    private stringSearchService: StringSearchService
  ) {}

  ngOnInit() {
    this.initSearch();
    this.initIsDisplayed();
    this.initInsets();
    this.initSelectPreviewed();
  }

  dismissModal() {
    this.dismissed$.next();
    this.focusEditor();
  }

  emitSelectedFavorite(favorite) {
    this.favoriteSelected.emit(favorite);
    this.dismissed$.next(); // dismiss immediately to prevent glitch in preview if the editor changes size
  }

  private initSearch() {
    // Emits lists of suggestions, correponding to the debounced search term
    // Null search terms trigger an empry list
    this.suggestions$ = this.codemirrorContext.cursorActivity$.pipe(
      map(() => this.getNormalizedSearchTerm()),
      distinctUntilChanged(),
      debounceTime(200),
      map(term => this.getSuggestions(term)),
      share(),
      startWith([]) // avoid testing for truthiness in template
    );

    this.previewedInEditor$ = this.suggestions$.pipe(
      map(suggestions => suggestions.find(suggestion => suggestion.doesStartWithTerm)),
      share()
    );
  }

  private initIsDisplayed() {
    const falseWhenDismissed$ = merge(this.dismissed$, this.codemirrorContext.escapePressed$).pipe(map(() => false));
    const hasSomeSuggestions$ = this.suggestions$.pipe(map(sugestions => sugestions.length > 0));

    this.isPopoverDisplayed$ = merge(falseWhenDismissed$, hasSomeSuggestions$).pipe(share());
  }

  private initInsets() {
    this.insets$ = this.isPopoverDisplayed$.pipe(
      filter(Boolean),
      map(() => this.getInsets())
    );
  }

  initSelectPreviewed() {
    this.subscriptions.add(
      this.codemirrorContext.tabPressed$
        .pipe(
          withLatestFrom(this.isPopoverDisplayed$, this.previewedInEditor$),
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          map(([pressed, isPopoverDisplayed, previewedInEditor]) =>
            isPopoverDisplayed && previewedInEditor ? previewedInEditor : null
          ),
          filter<FavoriteSuggestion>(Boolean)
        )
        .subscribe(suggestion => this.emitSelectedFavorite(suggestion.favorite))
    );
  }

  // Returns the content of the editor, in lowercase and without diacritics, iff
  //  - the editor content is between 3 and 20 characters long
  //  - only text and space has been entered in the editor (to avoid problems with links, mentions and hashtags)
  // Returns null otherwise
  private getNormalizedSearchTerm(): string | null {
    const editorContent = this.codemirrorContext.codeMirror.getValue();
    const lang = this.codemirrorContext.languageCode || 'fr';
    const contentWithoutDiacriticsAndLC = this.stringSearchService.removeDiacriticsThenLowerCase(editorContent, lang);
    return this.validTermRe.test(contentWithoutDiacriticsAndLC) ? contentWithoutDiacriticsAndLC : null;
  }

  private getSuggestions(normalizedTerm): FavoriteSuggestion[] {
    for (const favorite of this.favorites) {
      if (!favorite.normalizedText) {
        const lang = this.codemirrorContext.languageCode || 'fr';
        favorite.normalizedText = this.stringSearchService.removeDiacriticsThenLowerCase(
          favorite.text || favorite.editorModel?.text,
          lang
        );
      }
    }
    return normalizedTerm
      ? this.favorites
          .filter(
            favorite =>
              favorite.normalizedText.includes(normalizedTerm) && favorite.normalizedText.length > normalizedTerm.length
          )
          .map(
            favorite => new FavoriteSuggestion(favorite, normalizedTerm, this.codemirrorContext.codeMirror.getValue())
          )
          .slice(0, 6)
          .sort((s1, s2) => s2.sortWeight - s1.sortWeight)
      : [];
  }

  private getInsets() {
    // Layout our popover just above/below the containing <div>
    const parentRect: { left: number; right: number; top: number; bottom: number; height: number } =
      this.elementRef.nativeElement.parentElement.getBoundingClientRect();

    const popover = {
      top: 'auto',
      bottom: 'auto',
      left: `${parentRect.left}px`,
      right: `${this.basicService.window.innerWidth - parentRect.right}px`,
    };
    const isInUpperHalfOfViewport = parentRect.top + parentRect.bottom < this.basicService.window.innerHeight;
    if (isInUpperHalfOfViewport) {
      // position the popover below the editor
      popover.top = ` ${parentRect.top + parentRect.height}px`;
    } else {
      // position the popover above the editor
      popover.bottom = ` ${this.basicService.window.innerHeight - parentRect.top}px`;
    }
    const preview = {
      top: `${parentRect.top}px`,
      bottom: `${this.basicService.window.innerHeight - parentRect.bottom}px`,
      left: `${parentRect.left}px`,
      right: `${this.basicService.window.innerWidth - parentRect.right}px`,
    };
    return { popover, preview };
  }

  private focusEditor() {
    setTimeout(() => this.codemirrorContext.codeMirror.focus());
  }

  ngOnDestroy() {
    this.subscriptions?.unsubscribe();
  }
}
